import {CommonModule} from '@angular/common';
import {Component, ElementRef, HostListener, TemplateRef} from '@angular/core';
import {ButtonModule} from 'primeng/button';
import {DynamicDialogConfig} from 'primeng/dynamicdialog';
import {AppDialogRef} from './app-dialog-ref';

export interface AppTemplateDialogData {
	template: TemplateRef<unknown>;
	context?: unknown;
	appRef: AppDialogRef;
	showCloseButton?: boolean;
}

@Component({
	selector: 'ngx-app-template-dialog',
	standalone: true,
	imports: [CommonModule, ButtonModule],
	styles: [`
		:host {
			display: flex;
			flex-direction: column;
			flex: 1 1 auto;
			min-height: 0;
			max-height: 100%;
			width: 100%;
		}
	`],
	template: `
		<div class="app-dialog-frame">
			<p-button
				*ngIf="showCloseButton"
				type="button"
				class="app-dialog-close"
				icon="pi pi-times"
				size="small"
				[rounded]="true"
				[text]="true"
				severity="secondary"
				[attr.aria-label]="'Fermer'"
				(onClick)="close()"
			></p-button>
			<div class="app-dialog-scroll">
				<ng-container
					*ngTemplateOutlet="data.template; context: templateContext"
				></ng-container>
			</div>
		</div>
	`,
})
export class AppTemplateDialogComponent {
	constructor(
		public readonly config: DynamicDialogConfig<AppTemplateDialogData>,
		private readonly hostRef: ElementRef<HTMLElement>,
	) {
	}

	get data(): AppTemplateDialogData {
		return this.config.data;
	}

	get showCloseButton(): boolean {
		return this.data.showCloseButton !== false;
	}

	@HostListener('document:keydown', ['$event'])
	onDocumentKeydown(event: KeyboardEvent): void {
		if (event.key !== 'Escape' || this.config.closeOnEscape === false) {
			return;
		}

		const hostDialog = this.hostRef.nativeElement.closest('.p-dialog.app-dynamic-dialog') as HTMLElement | null;
		if (!hostDialog?.isConnected) {
			return;
		}

		const topDialog = AppTemplateDialogComponent.getTopDialog();
		if (!topDialog || topDialog !== hostDialog) {
			return;
		}

		event.preventDefault();
		event.stopImmediatePropagation();
		this.close();
	}

	close(): void {
		this.data.appRef.close();
	}

	get templateContext(): Record<string, unknown> {
		const ctx = this.data.context;
		const spread =
			typeof ctx === 'object' && ctx !== null && !Array.isArray(ctx)
				? {...(ctx as Record<string, unknown>)}
				: {};

		return {
			...spread,
			$implicit: ctx,
			dialogRef: this.data.appRef,
			ref: this.data.appRef,
		};
	}

	private static getTopDialog(): HTMLElement | null {
		const dialogs = Array.from(
			document.querySelectorAll<HTMLElement>('.p-dialog.app-dynamic-dialog'),
		);
		if (!dialogs.length) {
			return null;
		}

		return dialogs.reduce((top, dialog) => {
			const z = parseInt(dialog.style.zIndex || '0', 10);
			const topZ = parseInt(top.style.zIndex || '0', 10);
			return z >= topZ ? dialog : top;
		});
	}
}
